/**
 * PostHog Analytics
 *
 * Initialises PostHog for product analytics, feature flags, and session replay.
 * Safe to import anywhere — all calls are no-ops when the key is missing.
 */

import posthog from 'posthog-js'

const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY
const IS_DEV = import.meta.env.DEV

if (POSTHOG_KEY) {
  posthog.init(POSTHOG_KEY, {
    api_host: import.meta.env.VITE_POSTHOG_HOST || 'https://eu.i.posthog.com',
    autocapture: true,
    capture_pageview: true,
    capture_pageleave: true,
    persistence: 'localStorage+cookie',
    disable_session_recording: IS_DEV,
    loaded: (ph) => {
      if (IS_DEV) {
        ph.debug()
      }
    },
  })
}

/**
 * Identify a user after login. Call once per session.
 */
export function identifyUser(userId: string, properties?: {
  email?: string
  name?: string
  organization_id?: string
  organization_name?: string
  role?: string
}) {
  if (!POSTHOG_KEY) return
  posthog.identify(userId, properties)
}

/**
 * Reset identity on logout.
 */
export function resetUser() {
  if (!POSTHOG_KEY) return
  posthog.reset()
}

/**
 * Track a custom event.
 */
export function trackEvent(event: string, properties?: Record<string, unknown>) {
  if (!POSTHOG_KEY) return
  posthog.capture(event, properties)
}

/**
 * Set group (organisation-level analytics).
 */
export function setOrganization(orgId: string, properties?: Record<string, unknown>) {
  if (!POSTHOG_KEY) return
  posthog.group('organization', orgId, properties)
}

export { posthog }
